import React, { useEffect, useRef, useState } from "react";

import { faDown } from "@performyard/fontawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@performyard/fontawesome/react-fontawesome";
import { ButtonStandard, toast } from "@performyard/py-components";
import { AsyncTaskStatusUpdateEventType } from "@performyard/service-contracts";

import RadialProgress from "../DownloadButton/RadialProgress";

type DownloadButtonSSEProps = {
	task: AsyncTaskStatusUpdateEventType | null;
	onClick: () => Promise<void>;
	children: string;
	testid?: string;
};

const triggerFileDownload = (url: string): void => {
	const link = document.createElement("a");
	link.href = url;
	link.download = "";
	link.style.display = "none";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

export const DownloadButtonSSE = ({ task, onClick, children, testid }: DownloadButtonSSEProps) => {
	const [isInitializing, setIsInitializing] = useState(false);
	const lastDownloadedTaskIdRef = useRef<string | null>(null);

	const isRunning = isInitializing || task?.status === "queued" || task?.status === "processing";
	const progress = task && task.progressTotal > 0 ? task.progressCount / task.progressTotal : 0;

	const handleClick = async (): Promise<void> => {
		// Prevent clicks while task is running
		if (isRunning) {
			return;
		}

		setIsInitializing(true);

		try {
			await onClick();
		} catch (err) {
			console.error("[DownloadButtonSSE] Failed to start download:", err);
			toast.error("Failed to start download");
			setIsInitializing(false);
		}
	};

	// Handle task updates
	useEffect(() => {
		if (!task) return;

		// Stop showing initializing spinner once we get a NEW active task (not a stale completed one)
		if (isInitializing && (task.status === "queued" || task.status === "processing")) {
			setIsInitializing(false);
		}

		// Handle failed tasks
		if (task.status === "failed") {
			toast.error(task.error || "Download failed");
			setIsInitializing(false);
			return;
		}

		// Handle completed tasks - auto-download once per task
		if (task.status === "completed" && task.metadata?.signedUrl) {
			const taskId = task._id;

			// Only download if this is a new completed task
			if (taskId !== lastDownloadedTaskIdRef.current) {
				lastDownloadedTaskIdRef.current = taskId;
				triggerFileDownload(task.metadata.signedUrl as string);
				setIsInitializing(false);
			}
		}
	}, [task, isInitializing]);

	return (
		<ButtonStandard
			onClick={handleClick}
			variant="outline"
			color="neutral"
			disabled={isRunning}
			data-testid={testid}
		>
			<div className="flex items-center gap-2">
				<div className="relative h-[22px] w-[22px] rounded-full">
					<div
						className={`absolute inset-0 z-10 flex items-center justify-center text-xs ${
							isRunning ? "text-primary-5" : "text-neutral-3"
						}`}
					>
						<FontAwesomeIcon icon={faDown} />
					</div>
					<RadialProgress size={22} strokeWidth={2} percentage={isRunning ? progress * 100 : 0} />
				</div>

				<span>{isRunning ? "Downloading..." : children}</span>
			</div>
		</ButtonStandard>
	);
};

export default DownloadButtonSSE;
